import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { CXNoAuthApiService } from '~/components/ClientExperience/services/cx-no-auth-api.service';
import { CXApiService } from '~/components/ClientExperience/services/cx-api.service';
import { IUserResponse } from '~/types/server.types';
import { v2GetCurrentUserPath } from '~/utils/ts-routes.utils';
import { ISubmitCodeResponse } from '~/components/ClientExperience/types/server.types';
import { IRecaptchaTokenData } from '~/common/hooks/useHBGoogleReCaptcha';
import { ClientPortalConfigStore } from '~/components/ClientExperience/stores/client-portal-config.store';
import { S3ApiAdapter } from '~/components/ClientExperience/services/adapters/s3-api.adapter';
import { IUserConfig } from '~/components/ClientExperience/GiftCard/types/client-exprience.types';
import { ISubmitVerificationCodeProps } from '~/components/ClientExperience/controllers/user-cx.ctrl';

export interface ISendVerificationCode extends IRecaptchaTokenData {
    flowId: string;
    userEmail?: string;
    userId?: string;
    activate_recaptcha: boolean;
    fingerprint: string;
    smsConsentTextVersion?: string;
    plainTextEmail?: boolean;
}

export class UserCXAdapter extends BaseHttpAdapter {
    noAuthApiService: CXNoAuthApiService;
    clientPortalConfigStore: ClientPortalConfigStore;
    s3ApiAdapter: S3ApiAdapter;

    constructor({
        apiService,
        noAuthApiService,
        clientPortalConfigStore,
        s3ApiAdapter
    }: {
        apiService: CXApiService;
        clientPortalConfigStore: ClientPortalConfigStore;
        noAuthApiService: CXNoAuthApiService;
        s3ApiAdapter: S3ApiAdapter;
    }) {
        super(apiService);

        this.noAuthApiService = noAuthApiService;
        this.clientPortalConfigStore = clientPortalConfigStore;
        this.s3ApiAdapter = s3ApiAdapter;
    }

    async fetchUserConfig(placementId: string) {
        const { isPreview, s3PlacementFilesUrlPrefix } =
            this.clientPortalConfigStore;

        if (isPreview) {
            return null;
        }

        return this.s3ApiAdapter.apiRequest<IUserConfig>({
            url: `${s3PlacementFilesUrlPrefix}/websiteplacements/${placementId}/user_config_${placementId}.js?xhr=1`,
            isJsonp: true
        });
    }

    async sendVerificationCodeEmail({
        flowId: flow_id,
        recaptchaV3Token,
        recaptchaV2Token,
        userId: user_id,
        userEmail: user_email,
        activate_recaptcha,
        fingerprint,
        smsConsentTextVersion,
        plainTextEmail
    }: ISendVerificationCode) {
        return this.noAuthApiService.apiRequest({
            method: 'POST',
            url: '/api/v2/users/verification_code/email',
            params: {
                force_recaptcha: activate_recaptcha
            },
            data: {
                flow_id,
                'g-recaptcha-response-v3': recaptchaV3Token,
                'g-recaptcha-response': recaptchaV2Token,
                user_id,
                user_email,
                fingerprint,
                sms_consent_text_version: smsConsentTextVersion,
                is_plain_text_email: plainTextEmail
            }
        });
    }

    async submitVerificationCode({
        code,
        userId: user_id,
        userEmail: user_email,
        flowId: flow_id,
        fToken,
        fingerprint
    }: ISubmitVerificationCodeProps): Promise<ISubmitCodeResponse> {
        return this.noAuthApiService.apiRequest<ISubmitCodeResponse>({
            method: 'POST',
            url: '/api/v2/users/verification_code/submit',
            data: {
                fingerprint,
                code,
                user_id,
                user_email,
                flow_id,
                f_token: fToken
            }
        });
    }

    async validateEmail(email: string) {
        return this.noAuthApiService.apiRequest<ISubmitCodeResponse>({
            url: `/api/v2/users/validations/emails/${email}`
        });
    }

    async fetchUser(id: string) {
        return this.apiService.apiRequest<IUserResponse>({
            method: 'get',
            url: v2GetCurrentUserPath(id)
        });
    }

    async generateUserWorkspaceLink(userId: string, workspaceId: string) {
        return this.apiService.apiRequest<string>({
            method: 'get',
            url: `/api/v2/users/${userId}/link/${workspaceId}`
        });
    }
}
